import { ShareToolModal } from 'commerce-tools-ui-lib/public/components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useCallback, useMemo, useState, useRef } from 'react';
import { getQuoteId, getQuoteProperty } from 'quotes-ui-lib/utils/quote';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { getQuote, getQuotePdfLink } from '../api/QuotesApi';
import { getDealWithQuoteEmailCommunicatorUrl } from '../utils/urls';
import { getEmailShareTabInitialValues } from '../utils/email';
import { isImmutableContacts } from '../utils/isImmutable';
import { getEmailSendPreviewQuoteContextRpc, getEmailSendPreviewRpc, sendEmailRpc } from '../../__generated__/chirp/com/hubspot/commerce/tool/sender/rpc/CommerceToolSenderEmailService';
import useShareQuoteModalClose from '../hooks/useShareQuoteModalClose';
import QuotesLogger, { getTracker } from '../tracking/quotesLogger';
import { QUOTE } from 'customer-data-objects/constants/ObjectTypes';
import { rpcClient } from '../api/clients/rpcClientV2';
import { fetchQuoteEmailSettings } from '../api/quoteSettingsApi';
import { useConnectedEmails } from 'commerce-tools-ui-lib/public/hooks';
import { aiGenerateEmailContent } from '../api/AiQuoteEmailApi';
import { recordFeedbackRpc } from '../../__generated__/chirp/com/hubspot/commerce/ai/rpc/FeedbackService';
import { parseTextFromHTML, removeNewlines } from '../utils/utils';
import { isCPQQuote as getIsCPQQuote } from '../utils/templates';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ShareQuoteModal = ({
  quote,
  onQuoteUpdate,
  embeddedShowAlert,
  onClose,
  scopes,
  isQuoteCreated,
  trackingLocation,
  showCelebration = false,
  contacts: propsContacts,
  isUngatedForAiGenerateBuyerEmail
}) => {
  const [localQuote, setLocalQuote] = useState(quote);
  const emailPreviewQuoteContextRef = useRef();
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  // Using refs to avoid unnecessary re-renders for AI feedback, which doesn't
  // directly impact user experience
  const lastGeneratedBodyRef = useRef(null);
  const buyerEmailInferenceIdRef = useRef(null);
  const quoteId = getQuoteId(localQuote);
  const isCPQQuote = getIsCPQQuote(quote);
  const {
    handleOnClose,
    handleSendEmailOnClose
  } = useShareQuoteModalClose({
    onClose,
    embeddedShowAlert,
    isQuoteCreated
  });
  const emailSettingsConfig = useMemo(() => {
    return {
      query: fetchQuoteEmailSettings,
      canSendFromOtherEmailsDisabledTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.shareQuoteModal.emailShareTab.allowOtherEmailsDisabledTooltip"
      })
    };
  }, []);
  const {
    connectedEmailsLoading,
    connectedEmails
  } = useConnectedEmails(fetchQuoteEmailSettings);
  const getCopyLink = useCallback(() => {
    return getQuote(quoteId).then(quoteResponse => {
      const quoteLink = getQuoteProperty(quoteResponse, 'hs_quote_link');
      if (quoteLink) {
        onQuoteUpdate === null || onQuoteUpdate === void 0 || onQuoteUpdate(quoteResponse);
        setLocalQuote(quoteResponse);
      }
      return getQuoteProperty(quoteResponse, 'hs_quote_link');
    });
  }, [quoteId, onQuoteUpdate]);
  const getPdfLink = useCallback(() => {
    return getQuotePdfLink(quoteId);
  }, [quoteId]);
  const loadEmailPreview = useCallback(async ({
    body
  }) => {
    if (!emailPreviewQuoteContextRef.current) {
      const emailSendPreviewQuoteContextResponse = await rpcClient.call(getEmailSendPreviewQuoteContextRpc, {
        request: {
          commerceToolType: 'QUOTE',
          commerceToolId: Number(quoteId),
          emailRequestTemplateType: 'QUOTE_SEND'
        }
      });
      emailPreviewQuoteContextRef.current = emailSendPreviewQuoteContextResponse.emailPreviewQuoteContext;
    }
    const emailSendPreviewResponse = await rpcClient.call(getEmailSendPreviewRpc, {
      request: {
        commerceToolType: 'QUOTE',
        commerceToolId: Number(quoteId),
        emailRequestTemplateType: 'QUOTE_SEND',
        emailContent: {
          subject: 'test',
          body
        },
        emailPreviewQuoteContext: emailPreviewQuoteContextRef.current
      }
    });
    return Promise.resolve({
      html: emailSendPreviewResponse.messageAsHtml
    });
  }, [quoteId, emailPreviewQuoteContextRef]);
  const handleAiGenerateEmailContent = useCallback(aiGenerateEmailParams => {
    QuotesLogger.log('quoteInteraction', {
      action: 'Generate quote buyer email',
      location: trackingLocation || ''
    });
    return aiGenerateEmailContent(Object.assign({}, aiGenerateEmailParams, {
      quoteId
    })).then(({
      subject,
      body,
      inferenceId
    }) => {
      // Store the generated body content for later comparison
      lastGeneratedBodyRef.current = body;
      buyerEmailInferenceIdRef.current = inferenceId;
      return {
        subject,
        body
      };
    }).catch(__error => {
      if (embeddedShowAlert) {
        embeddedShowAlert({
          titleText: unescapedText('quotesUiLib.shareQuoteModal.emailShareTab.aiGenerateButton.error.title'),
          message: unescapedText('quotesUiLib.shareQuoteModal.emailShareTab.aiGenerateButton.error.message'),
          type: 'error'
        });
      }
      return {
        subject: null,
        body: null
      };
    });
  }, [quoteId, embeddedShowAlert, trackingLocation]);

  /**
   * Sends feedback about whether the AI-generated email content was used without modifications
   * @param emailBody The body of the email to send feedback about
   */
  const sendEmailAiFeedback = useCallback(emailBody => {
    const wasAiGenerated = buyerEmailInferenceIdRef.current !== null;
    if (!wasAiGenerated) {
      // No feedback to send if email wasn't AI generated
      return;
    }

    // The text we parse from the email body will not contain newlines, so we need to remove them from
    // the original AI-generated content before comparing
    const parsedEmailBodyText = parseTextFromHTML(emailBody);
    const wasAiContentModified = removeNewlines(lastGeneratedBodyRef.current) !== parsedEmailBodyText;

    // Only send feedback if the content has been modified
    // (ignoring editor-specific formatting changes)
    if (wasAiContentModified) {
      // Send feedback RPC in parallel - no need to await its result
      rpcClient.call(recordFeedbackRpc, {
        request: {
          inferenceId: buyerEmailInferenceIdRef.current,
          feedback: parseTextFromHTML(emailBody)
        }
      }).catch(error => {
        // Just log errors from feedback, don't interrupt the main flow
        console.error('Failed to send AI feedback:', error);
      });
    }
  }, []);
  const sendEmail = useCallback((emailSendValues, onSuccess, onError) => {
    var _emailSendValues$from;
    sendEmailAiFeedback(emailSendValues.body);
    rpcClient.call(sendEmailRpc, {
      request: {
        commerceToolType: 'QUOTE',
        commerceToolId: Number(quoteId),
        emailRequestTemplateType: 'QUOTE_SEND',
        toEmail: emailSendValues.toContact.properties.email.value,
        ccEmails: emailSendValues.ccContacts.map(ccContact => ccContact.properties.email.value),
        includePdfAttachment: true,
        fromEmail: emailSendValues.fromValue.email,
        fromName: (_emailSendValues$from = emailSendValues.fromValue.name) !== null && _emailSendValues$from !== void 0 ? _emailSendValues$from : undefined,
        emailContent: {
          subject: emailSendValues.subject,
          body: emailSendValues.body
        },
        emailSenderType: 'USER'
      }
    }).then(() => {
      onSuccess();
      handleSendEmailOnClose();
    }).catch(__error => {
      onError({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.alert.error.titleText"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.alert.error.message"
        })
      });
    });
  }, [quoteId, handleSendEmailOnClose, sendEmailAiFeedback]);
  const linkOrPdfShareTab = useMemo(() => {
    const props = {
      title: unescapedText('quotesUiLib.shareQuoteModal.pdfOrLinkTab.title'),
      downloadPdfProps: {
        getPdfLink,
        failedAlertTitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.downloadPdf.alert.title"
        }),
        failedAlertMessage: refetchLink => /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.downloadPdf.alert.message_jsx",
          elements: {
            Link: Button
          },
          options: {
            linkProps: {
              use: 'link',
              onClick: refetchLink
            }
          }
        })
      },
      copyLinkProps: {
        getCopyLink,
        loadingMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.copyLink.loading"
        }),
        failedAlertTitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.copyLink.alert.title"
        }),
        failedAlertMessage: refetchLink => /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.copyLink.alert.message_jsx",
          elements: {
            Link: Button
          },
          options: {
            linkProps: {
              use: 'link',
              onClick: refetchLink
            }
          }
        })
      },
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.shareQuoteModal.pdfOrLinkTab.description"
      }),
      previewComponent: /*#__PURE__*/_jsxs(_Fragment, {
        children: [isIframeLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
          contextual: true,
          layout: "centered",
          size: "medium"
        }), !!getQuoteProperty(localQuote, 'hs_quote_link') &&
        /*#__PURE__*/
        /* @bend-expect trellis-tokenized */
        _jsx("iframe", {
          src: getQuoteProperty(localQuote, 'hs_quote_link'),
          style: {
            width: '100%',
            height: '100%',
            border: 'none'
          },
          hidden: isIframeLoading,
          onLoad: () => setIsIframeLoading(false)
        })]
      })
    };
    return props;
  }, [getCopyLink, getPdfLink, localQuote, isIframeLoading]);
  const contacts = propsContacts !== null && propsContacts !== void 0 ? propsContacts : getQuoteProperty(localQuote, 'recipientContacts');
  const emailShareTabInitialValues = useMemo(() => {
    return getEmailShareTabInitialValues(localQuote, isImmutableContacts(contacts) ? contacts.toJS() : contacts);
  }, [localQuote, contacts]);
  const emailShareTab = useMemo(() => {
    const config = {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.shareQuoteModal.emailShareTab.title"
      }),
      emailNotConnectedDescription: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.shareQuoteModal.emailShareTab.emailNotConnectedDescription"
      }),
      initialValues: emailShareTabInitialValues,
      emailSettingsConfig,
      sendEmailButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotesUiLib.shareQuoteModal.emailShareTab.sendButton"
      }),
      manualEmailButtonConfig: {
        emailCommunicatorUrl: getDealWithQuoteEmailCommunicatorUrl(localQuote),
        buttonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.emailShareTab.crmEmailButton.label"
        }),
        tooltipMainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.emailShareTab.crmEmailButton.tooltip"
        }),
        // If quote is still publishing and no email URL is available, disable the button
        disabled: !getDealWithQuoteEmailCommunicatorUrl(localQuote)
      },
      loadEmailPreview,
      sendEmail
    };
    return isCPQQuote && isUngatedForAiGenerateBuyerEmail ? Object.assign({}, config, {
      aiGenerateEmailButtonConfig: {
        buttonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.emailShareTab.aiGenerateButton.label"
        }),
        tooltipMainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quotesUiLib.shareQuoteModal.emailShareTab.aiGenerateButton.tooltip"
        }),
        generateSubjectAndBody: handleAiGenerateEmailContent
      }
    }) : config;
  }, [emailSettingsConfig, emailShareTabInitialValues, loadEmailPreview, localQuote, sendEmail, handleAiGenerateEmailContent, isCPQQuote, isUngatedForAiGenerateBuyerEmail]);
  const trackerProps = useMemo(() => {
    return {
      tracker: getTracker(),
      trackingProperties: {
        location: trackingLocation,
        objectType: QUOTE
      }
    };
  }, [trackingLocation]);
  return /*#__PURE__*/_jsx(ShareToolModal, {
    headerTitle: isQuoteCreated ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "quotesUiLib.shareQuoteModal.header.created"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "quotesUiLib.shareQuoteModal.header.send"
    }),
    defaultSelectedTab: !isQuoteCreated || connectedEmails.length > 0 ? 'emailShareTab' : 'linkOrPdfShareTab',
    isLoading: isQuoteCreated && connectedEmailsLoading,
    showCelebration: showCelebration,
    hasScope: scope => scopes.has(scope),
    onClose: handleOnClose,
    tabs: {
      emailShareTab,
      linkOrPdfShareTab
    },
    trackerProps: trackerProps
  });
};
export default ShareQuoteModal;